import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import { getOffsetDate, startOfDay } from '@app/core/util/date_utils';
import { Popover } from 'primeng/popover';
import {
  NotificationAlertBulkActionValue,
  NotificationAlertFilterValue,
  INotificationAlertBulkActionOption,
  INotificationAlertFilterOption,
} from '@app/account/notifications/interfaces/notifications-data.interfaces';
import {
  ChannelNotification,
  NotificationRuleSeverityEnum,
  NotificationSlugEnum,
} from '@app/generated/graphql';
import { NotificationIconConfig } from '@app/account/notifications-and-alerts/notification.icon.config';
import { NotificationPreferencesAnalyticsService } from '@app/account/notification-preferences/services/notification-preferences-analytics.service';
import { NotificationPreferencesConfigService } from '@app/account/notification-preferences/services/notification-preferences-config.service';
import { Bind } from 'primeng/bind';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { AngularSvgIconModule } from 'angular-svg-icon';
import {
  NgClass,
  NgTemplateOutlet,
  NgPlural,
  NgPluralCase,
} from '@angular/common';
import { Drawer } from 'primeng/drawer';
import { Listbox } from 'primeng/listbox';
import { FormsModule } from '@angular/forms';
import { PrimeTemplate } from 'primeng/api';
import { Ag1LoaderComponent } from '../../../../core/components/ag1-loader/ag1-loader.component';
import { Ag1TimeSinceComponent } from '../../../../core/components/ag1-time-since/ag1-time-since.component';

@Component({
  selector: 'app-notifications-v2',
  templateUrl: './notifications-v2.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    Bind,
    Button,
    Tooltip,
    AngularSvgIconModule,
    Popover,
    NgClass,
    NgTemplateOutlet,
    NgPlural,
    NgPluralCase,
    Drawer,
    Listbox,
    FormsModule,
    PrimeTemplate,
    Ag1LoaderComponent,
    Ag1TimeSinceComponent,
  ],
})
export class NotificationsV2Component {
  // dependencies
  private _router: Router = inject(Router);
  private _notificationIconConfig = inject(NotificationIconConfig);
  private _analyticsService = inject(NotificationPreferencesAnalyticsService);
  private _notificationPreferencesConfig = inject(
    NotificationPreferencesConfigService,
  );

  // inputs
  layout = input<'menu' | 'standalone'>('standalone');
  notifications = input<ChannelNotification[]>([]);
  isLoading = input<boolean>(false);
  loadingMore = input<boolean>(false);
  hasNextPage = input<boolean>(false);
  totalNotificationsCount = input<number>(0);
  unreadNotificationsCount = input<number>(0);
  filteredNotificationsCount = input<number>(0);

  // outputs
  notificationCountChange = output<number>();
  markAllAsRead = output<string[]>();
  archiveAllRead = output<string[]>();
  archiveAll = output<string[]>();
  filterNotifications = output<NotificationAlertFilterValue>();
  loadMore = output<void>();

  markNotificationAsRead = output<ChannelNotification>();
  markNotificationAsUnread = output<ChannelNotification>();
  archiveNotification = output<ChannelNotification>();
  notificationClick = output<ChannelNotification>();

  notificationPopover = viewChild<Popover>('notificationPopover');
  notificationFilterPopover = viewChild<Popover>('notificationFilterPopover');
  notificationBulkActionsPopover = viewChild<Popover>(
    'notificationBulkActionsPopover',
  );

  // signals
  notificationFilterPopoverVisible = signal<boolean>(false);
  notificationBulkActionsPopoverVisible = signal<boolean>(false);
  notificationPopoverVisible = signal<boolean>(false);
  drawerVisible = signal<boolean>(false);
  private _notificationInboxOpenedAt = signal<Date | null>(null);

  // shared date boundaries
  private _startOfToday = computed(() => startOfDay(new Date()));
  private _startOfYesterday = computed(() => startOfDay(getOffsetDate(1)));

  // computed signals
  notificationsCount = computed(() => this.notifications()?.length ?? 0);

  notificationsTitle = computed(() => {
    return this.selectedFilter()?.value ===
      NotificationAlertFilterValue.Archived
      ? 'Archived Notifications'
      : 'Notifications';
  });

  isMenuLayout = computed(() => this.layout() === 'menu');
  isStandaloneLayout = computed(() => this.layout() === 'standalone');
  isSidepanelLayout = computed(
    () => this.isStandaloneLayout() && this.totalNotificationsCount() >= 6,
  );

  notificationsToday = computed(() => {
    const startOfToday: Date = this._startOfToday();
    return this.notifications()?.filter((notification: ChannelNotification) => {
      return (
        notification?.deliveredAt &&
        new Date(notification.deliveredAt) >= startOfToday
      );
    });
  });

  notificationsYesterday = computed(() => {
    const startOfToday: Date = this._startOfToday();
    const startOfYesterday: Date = this._startOfYesterday();

    return this.notifications()?.filter((notification: ChannelNotification) => {
      if (!notification?.deliveredAt) return false;
      const deliveredAt: Date = new Date(notification.deliveredAt);
      return deliveredAt >= startOfYesterday && deliveredAt < startOfToday;
    });
  });

  notificationsOlder = computed(() => {
    const startOfYesterday: Date = this._startOfYesterday();
    return this.notifications()?.filter((notification: ChannelNotification) => {
      return (
        notification?.deliveredAt &&
        new Date(notification.deliveredAt) < startOfYesterday
      );
    });
  });

  showFilterIndicator = computed(() => {
    const value = this.selectedFilter()?.value;
    return (
      value === NotificationAlertFilterValue.UnreadOnly ||
      value === NotificationAlertFilterValue.Archived
    );
  });

  filterOptions: INotificationAlertFilterOption[] = [
    {
      label: 'Unread & read',
      value: NotificationAlertFilterValue.UnreadAndRead,
      iconUrl: 'assets/icons/general/action-list.svg',
    },
    {
      label: 'Unread only',
      value: NotificationAlertFilterValue.UnreadOnly,
      iconUrl: 'assets/icons/general/communication-unread.svg',
    },
    {
      label: 'Archived',
      value: NotificationAlertFilterValue.Archived,
      iconUrl: 'assets/icons/general/action-archive.svg',
    },
  ];

  bulkActionOptions: INotificationAlertBulkActionOption[] = [
    {
      label: 'Mark all as read',
      value: NotificationAlertBulkActionValue.MarkAllRead,
      iconUrl: 'assets/icons/general/system-feedback-success.svg',
    },
    {
      label: 'Archive all',
      value: NotificationAlertBulkActionValue.ArchiveAll,
      iconUrl: 'assets/icons/general/action-archive.svg',
    },
    {
      label: 'Archive all read',
      value: NotificationAlertBulkActionValue.ArchiveAllRead,
      iconUrl: 'assets/icons/general/action-archive.svg',
    },
  ];

  NotificationAlertFilterValue = NotificationAlertFilterValue;

  // selections
  selectedFilter = signal<INotificationAlertFilterOption | null>(
    this.filterOptions[0],
  );
  selectedBulkAction = signal<INotificationAlertBulkActionOption | null>(null);

  constructor() {
    effect(() => {
      this.notificationCountChange.emit(this.notificationsCount());
    });

    effect(() => {
      if (!this.drawerVisible()) {
        this.selectedFilter.set(this.filterOptions[0]);
        this.filterNotifications.emit(
          NotificationAlertFilterValue.UnreadAndRead,
        );
      }
    });
  }

  onScroll(event: Event): void {
    const target: HTMLElement | null = event.target as HTMLElement;
    if (!target) return;

    const threshold: number = 100;
    const distanceFromBottom: number =
      target.scrollHeight - target.scrollTop - target.clientHeight;

    if (
      distanceFromBottom < threshold &&
      this.hasNextPage() &&
      !this.loadingMore()
    ) {
      this.loadMore.emit();
    }
  }

  onStandaloneButtonClick(event: MouseEvent): void {
    if (!event) return;

    if (this.isSidepanelLayout()) {
      this.drawerVisible.update((visible: boolean) => !visible);
      this.notificationPopover()?.hide();
    } else {
      this.notificationPopover()?.toggle(event);
      this.drawerVisible.set(false);
    }
  }

  onNotificationPopoverShow(): void {
    this.notificationPopoverVisible.set(true);

    this._notificationInboxOpenedAt.set(new Date());
    this._analyticsService.trackNotificationCenterOpened({
      unreadCount: this.unreadNotificationsCount(),
    });

    this._analyticsService.trackNotificationHistoryViewed({
      totalCount: this.totalNotificationsCount(),
      unreadCount: this.unreadNotificationsCount(),
    });
  }

  onNotificationPopoverHide(): void {
    this.notificationPopoverVisible.set(false);

    // reset filter to unread and read
    this.selectedFilter.set(this.filterOptions[0]);
    this.filterNotifications.emit(NotificationAlertFilterValue.UnreadAndRead);

    this._trackNotificationCenterClosed();
  }

  onNotificationFilterPopoverShow(): void {
    this.notificationFilterPopoverVisible.set(true);
  }

  onNotificationFilterPopoverHide(): void {
    this.notificationFilterPopoverVisible.set(false);
  }

  onNotificationBulkActionsPopoverShow(): void {
    this.notificationBulkActionsPopoverVisible.set(true);
  }

  onNotificationBulkActionsPopoverHide(): void {
    this.notificationBulkActionsPopoverVisible.set(false);
  }

  onFilterButtonClick(event: MouseEvent): void {
    if (!event) return;

    if (this.notificationBulkActionsPopoverVisible()) {
      this.notificationBulkActionsPopover()?.hide();
    }

    this.notificationFilterPopover()?.toggle(event);
  }

  onFilterSelectionChange(
    selectedFilter: INotificationAlertFilterOption | null,
  ): void {
    if (!selectedFilter) return;

    this.selectedFilter.set(selectedFilter);

    this.filterNotifications.emit(selectedFilter.value);

    this.notificationFilterPopover()?.hide();

    this._analyticsService.trackNotificationHistoryFiltered({
      filterValue: selectedFilter.label,
      resultsCount: this.filteredNotificationsCount(),
    });
  }

  onBulkActionsButtonClick(event: MouseEvent): void {
    if (!event) return;

    if (this.notificationFilterPopoverVisible()) {
      this.notificationFilterPopover()?.hide();
    }

    this.notificationBulkActionsPopover()?.toggle(event);
  }

  onBulkActionSelectionChange(
    selected: INotificationAlertBulkActionOption | null,
  ): void {
    const value: NotificationAlertBulkActionValue | undefined = selected?.value;

    if (!value) return;

    if (!this.notifications()?.length) return;

    switch (value) {
      case NotificationAlertBulkActionValue.MarkAllRead:
        this.onMarkAllAsRead();
        break;
      case NotificationAlertBulkActionValue.ArchiveAllRead:
        this.onArchiveAllRead();
        break;
      case NotificationAlertBulkActionValue.ArchiveAll:
        this.onArchiveAll();
        break;
    }

    this.selectedBulkAction.set(null);
    this.notificationBulkActionsPopover()?.hide();
  }

  onMarkAllAsRead(): void {
    const ids: string[] = this.notifications()
      .filter((notification: ChannelNotification) => !notification?.readAt)
      .map((notification: ChannelNotification) => notification.id);

    if (!ids?.length) return;

    this._analyticsService.trackNotificationsBatchAction({
      actionType: 'mark_all_read',
      affectedCount: ids.length,
    });

    this.markAllAsRead.emit(ids);
  }

  onArchiveAllRead(): void {
    const ids: string[] = this.notifications()
      .filter(
        (notification: ChannelNotification) =>
          notification?.readAt && !notification?.archivedAt,
      )
      .map((notification: ChannelNotification) => notification.id);

    if (!ids?.length) return;

    this._analyticsService.trackNotificationsBatchAction({
      actionType: 'archive_all_read',
      affectedCount: ids.length,
    });

    this.archiveAllRead.emit(ids);
  }

  onArchiveAll(): void {
    const ids: string[] = this.notifications()
      .filter((notification: ChannelNotification) => !notification?.archivedAt)
      .map((notification: ChannelNotification) => notification.id);

    if (!ids?.length) return;

    this._analyticsService.trackNotificationsBatchAction({
      actionType: 'archive_all',
      affectedCount: ids.length,
    });

    this.archiveAll.emit(ids);
  }

  openSettings(): void {
    this._router.navigate([
      '/account/notification-preferences/general-preferences',
    ]);

    this._hideView();
  }

  onToggleNotificationMarkAsRead(notification: ChannelNotification): void {
    if (!notification) return;

    if (!notification?.readAt) {
      this.markNotificationAsRead.emit(notification);
    } else {
      this.markNotificationAsUnread.emit(notification);
    }
  }

  onArchiveNotification(notification: ChannelNotification): void {
    if (!notification) return;

    this.archiveNotification.emit(notification);
  }

  onNotificationClick(notification: ChannelNotification): void {
    if (!notification) {
      return;
    }

    this._hideView();
    this.notificationClick.emit(notification);

    const deliveredAt: Date = new Date(notification.deliveredAt);

    const timeSinceReceivedSeconds: number =
      Math.round((Date.now() - deliveredAt.getTime()) / 1000) || 0;

    this._analyticsService.trackNotificationClicked({
      notification: {
        id: notification.id,
        notificationSlug:
          notification.notificationRule?.notificationSlug ??
          NotificationSlugEnum.Example,
        name: notification.notificationRule?.name ?? '',
        description: notification.notificationRule?.description ?? '',
        severity: NotificationRuleSeverityEnum.Info,
      },
      timeSinceReceivedSeconds,
      source: 'history',
    });
  }

  private _hideView(): void {
    this.notificationPopover()?.hide();
    this.notificationFilterPopover()?.hide();
    this.notificationBulkActionsPopover()?.hide();

    this.drawerVisible.set(false);
  }

  getNotificationIcon(notification: ChannelNotification): string {
    return this._notificationIconConfig.getIcon(
      notification.notificationRule.severity,
    );
  }

  /** Delegates to NotificationPreferencesConfigService for dynamic title/body (single source of truth). */
  resolvedChannelNotificationCopy(notification: ChannelNotification): {
    title: string;
    body: string;
  } {
    return this._notificationPreferencesConfig.getResolvedChannelNotificationCopy(
      notification,
    );
  }

  onNotificationDrawerShow(): void {
    this._notificationInboxOpenedAt.set(new Date());

    this._analyticsService.trackNotificationCenterOpened({
      unreadCount: this.unreadNotificationsCount(),
    });

    this._analyticsService.trackNotificationHistoryViewed({
      totalCount: this.totalNotificationsCount(),
      unreadCount: this.unreadNotificationsCount(),
    });
  }

  onNotificationDrawerHide(): void {
    this._trackNotificationCenterClosed();
  }

  private _trackNotificationCenterClosed(): void {
    const openedAt: Date | null = this._notificationInboxOpenedAt();
    if (!openedAt) return;

    const timeOpenSeconds: number = Math.round(
      (Date.now() - openedAt.getTime()) / 1000,
    );

    this._analyticsService.trackNotificationCenterClosed({
      timeOpenSeconds,
    });

    this._notificationInboxOpenedAt.set(null);
  }
}
